<script setup lang="ts">
import { computed } from 'vue';
import type { LuggageLocationResponse } from '@/entities/application/api/fetchLuggageLocation';
import { formatDateToJst, formatTimeToJst } from '@/entities/application/lib/datetime';

type DeliveryLocation = LuggageLocationResponse['delivery_locations'][number];
type RouteStep = DeliveryLocation['steps'][number];

const DELIVERY_RECORD_TYPE_LABELS = {
    reception: '受付',
    loaded: '積み込み',
    unloaded: '荷下ろし',
    delivered: '受け渡し',
    inspected: '検品',
    guest_reception: 'ゲスト受領',
} as const satisfies Record<RouteStep['record_type'], string>;

export type Props = {
    deliveryLocations?: readonly DeliveryLocation[];
    defaultOpen?: boolean;
    disabled?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
    deliveryLocations: () => [],
    defaultOpen: false,
    disabled: false,
});

const hasLocationData = computed(() => props.deliveryLocations.length > 0);

const getLocationTransition = (
    step: RouteStep,
    index: number,
    steps: readonly RouteStep[]
): { from: string; to: string } => {
    const previousStep = index > 0 ? steps[index - 1] : null;
    const fromLocation = previousStep ? previousStep.vehicle_name || previousStep.location_name : 'ゲスト';
    const toLocation = step.vehicle_name || step.location_name;
    return { from: fromLocation, to: toLocation };
};

const getRecordTypeLabel = (recordType: RouteStep['record_type']): string => {
    return DELIVERY_RECORD_TYPE_LABELS[recordType] || recordType;
};
</script>

<template>
    <v-expansion-panels :model-value="defaultOpen ? [0] : []" class="mb-4">
        <v-expansion-panel>
            <v-expansion-panel-title :disabled="disabled">
                <div class="d-flex align-center">
                    <v-icon class="me-2">mdi-map-marker-path</v-icon>
                    <span class="text-subtitle-1 font-weight-medium">配送ロケーション情報</span>
                    <v-chip v-if="hasLocationData" size="small" color="success" class="ms-2">
                        {{ deliveryLocations.length }}件
                    </v-chip>
                </div>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
                <div class="pa-2">
                    <!-- 履歴データがある場合 -->
                    <div v-if="hasLocationData" class="delivery-locations">
                        <div
                            v-for="delivery in deliveryLocations"
                            :key="delivery.luggage_id"
                            class="luggage-delivery-section"
                        >
                            <div class="luggage-header">
                                <v-icon class="me-2" color="primary">mdi-qrcode</v-icon>
                                <span class="font-weight-medium">荷物QRコード: {{ delivery.luggage_qr_code }}</span>
                            </div>

                            <v-table density="compact" class="delivery-table desktop-only">
                                <thead>
                                    <tr>
                                        <th class="text-left">ロケーション</th>
                                        <th class="text-left">担当/作業</th>
                                        <th class="text-left">荷物スキャン</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(step, index) in delivery.steps"
                                        :key="step.sequence"
                                        :class="{ 'completed-row': step.recorded_at, 'pending-row': !step.recorded_at }"
                                    >
                                        <td class="location-cell">
                                            <div class="cell-stack">
                                                <span class="cell-sub">{{
                                                    getLocationTransition(step, index, delivery.steps).from
                                                }}</span>
                                                <span class="cell-main"
                                                    >→ {{ getLocationTransition(step, index, delivery.steps).to }}</span
                                                >
                                            </div>
                                        </td>
                                        <td class="work-cell">
                                            <div class="cell-stack">
                                                <span v-if="step.staff_name" class="cell-sub">{{
                                                    step.staff_name
                                                }}</span>
                                                <span v-else class="cell-sub">-</span>
                                                <span class="cell-main">{{
                                                    getRecordTypeLabel(step.record_type)
                                                }}</span>
                                            </div>
                                        </td>
                                        <td class="datetime-cell">
                                            <div v-if="step.recorded_at" class="cell-stack">
                                                <span class="cell-sub">{{ formatDateToJst(step.recorded_at) }}</span>
                                                <span class="cell-main">{{ formatTimeToJst(step.recorded_at) }}</span>
                                            </div>
                                            <span v-else class="cell-sub">-</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-table>

                            <div class="mobile-cards mobile-only">
                                <div
                                    v-for="(step, index) in delivery.steps"
                                    :key="step.sequence"
                                    class="step-card"
                                    :class="{ 'step-card--pending': !step.recorded_at }"
                                >
                                    <div class="step-card__location">
                                        <span class="cell-sub">{{
                                            getLocationTransition(step, index, delivery.steps).from
                                        }}</span>
                                        <span class="cell-main"
                                            >→ {{ getLocationTransition(step, index, delivery.steps).to }}</span
                                        >
                                    </div>
                                    <div class="step-card__row">
                                        <span v-if="step.staff_name" class="cell-sub">{{ step.staff_name }}</span>
                                        <span v-else class="cell-sub">-</span>
                                        <span class="cell-main">{{ getRecordTypeLabel(step.record_type) }}</span>
                                    </div>
                                    <div class="step-card__row">
                                        <span v-if="step.recorded_at" class="cell-sub">{{
                                            formatDateToJst(step.recorded_at)
                                        }}</span>
                                        <span v-else class="cell-sub">-</span>
                                        <span v-if="step.recorded_at" class="cell-main">{{
                                            formatTimeToJst(step.recorded_at)
                                        }}</span>
                                        <span v-else class="cell-main">-</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 履歴データがない場合 -->
                    <div v-else class="text-center py-4">
                        <v-icon size="48" color="grey-lighten-1" class="mb-2">mdi-map-marker-off</v-icon>
                        <p class="text-body-2 text-grey">ロケーション履歴がありません</p>
                    </div>
                </div>
            </v-expansion-panel-text>
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<style scoped>
.delivery-locations {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.luggage-delivery-section {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
}

.luggage-header {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background-color: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
}

.delivery-table {
    border: none;
}

.delivery-table th:nth-child(2),
.delivery-table th:nth-child(3) {
    width: 20%;
}

.delivery-table th {
    background-color: #fafafa !important;
    font-weight: 600;
}

.pending-row td {
    color: #9e9e9e;
}

.location-cell,
.work-cell,
.datetime-cell {
    padding: 8px 12px !important;
    vertical-align: middle;
}

.cell-stack {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.cell-main {
    font-size: 0.875rem;
    font-weight: 500;
    color: #1a1a1a;
    line-height: 1.4;
}

.cell-sub {
    font-size: 0.8125rem;
    color: #9e9e9e;
    line-height: 1.4;
}

.pending-row .cell-main,
.pending-row .cell-sub {
    color: #bdbdbd;
}

.desktop-only {
    display: block;
}

.mobile-only {
    display: none !important;
}

@media (max-width: 600px) {
    .desktop-only {
        display: none !important;
    }

    .mobile-only {
        display: flex !important;
        flex-direction: column;
    }

    :deep(.v-expansion-panel-text__wrapper) {
        padding-inline: 8px;
    }
}

.mobile-cards {
    display: flex;
    flex-direction: column;
    padding: 12px;
}

.step-card {
    background: #fff;
    padding: 12px 0;
}

.step-card:first-child {
    padding-top: 0;
}

.step-card:last-child {
    padding-bottom: 0;
}

.step-card + .step-card {
    border-top: 1px solid #e5e7eb;
}

.step-card--pending {
    background: #fafafa;
}

.step-card--pending .cell-main,
.step-card--pending .cell-sub,
.step-card--pending .step-card__work,
.step-card--pending .step-card__datetime {
    color: #bdbdbd;
}

.step-card__location {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.step-card__row {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 8px;
}
</style>
